import { FEVER_API_ENDPOINT } from 'config/envVariable';
import { fetchOpts, newFetchOpts } from './common';
import { getFirebaseIdToken, toLoginPage } from 'editor/utils/firebaseManager';

export async function getFormVersion(formId) {
    return fetch(`${FEVER_API_ENDPOINT}/forms/${formId}/version`, newFetchOpts()).then(res =>
        res.json().then(payload => ({ status: res.status, payload }))
    );
}

export function initEditorForm(formVersion, formId, sponsorId) {
    return formVersion === 0 ? initOldEditorForm(formId) : initNewEditorForm(formId, sponsorId);
}

async function initOldEditorForm(id) {
    const token = await getFirebaseIdToken();
    return fetch(`${FEVER_API_ENDPOINT}/form/${id}/edit`, fetchOpts({ method: 'GET', token }))
        .then(res => {
            // hasPermission(res.status);
            return res.json();
        })
        .then(mergePromoSettingInFormData);
}

async function initNewEditorForm(id, sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(`${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${id}`, newFetchOpts({ token }))
        .then(res => {
            hasPermission(res.status);
            return res.json();
        })
        .then(mergePromoSettingInFormData);
}

export async function mergePromoSettingInFormData(formResp) {
    if (formResp.code === 200) {
        const { sponsorId } = formResp.items;
        const { promotionId, promotionUuid } = formResp.items.form;
        if (promotionId || promotionUuid) {
            try {
                if (promotionId && promotionUuid) {
                    // new-promotion
                    const [promoInfo, promoSetting] = await Promise.all([
                        getNewPromoInfo(sponsorId, promotionId),
                        getNewPromoSetting(sponsorId, promotionId)
                    ]);
                    const { status: promoOnlineStatus } = promoInfo.data;
                    const {
                        Styles: {
                            bgType = 1,
                            bgColor,
                            bgImgUrl,
                            contentBgType,
                            contentBgColor,
                            contentBgImgUrl,
                            buttonBgColor,
                            buttonTextColor,
                            optionBgColor,
                            optionTextColor,
                            titleColor,
                            descColor,
                            answerColor
                        } = {}
                    } = promoSetting.data;
                    formResp.items.promoSettings = {
                        promoOnlineStatus,
                        defaultBg: {
                            type: bgType,
                            color: bgColor,
                            imageUrl: bgImgUrl
                        },
                        defaultContentBg: contentBgType
                            ? {
                                  type: contentBgType,
                                  color: contentBgColor,
                                  imageUrl: contentBgImgUrl
                              }
                            : undefined,
                        defaultTextColor: titleColor,
                        defaultDescColor: descColor,
                        defaultAnwsColor: answerColor,
                        defaultOptionColor: optionBgColor,
                        defaultOptionLabelColor: optionTextColor,
                        defaultBtnBgColor: buttonBgColor,
                        defaultBtnTextColor: buttonTextColor
                    };
                } else {
                    // old-promotion
                    const promoSettings = await getPromoSetting(promotionId);
                    if (promoSettings.code !== 200) {
                        throw promoSettings;
                    }
                    const promoDatas = promoSettings.items;
                    formResp.items.promoSettings = { ...promoDatas };
                }
            } catch (error) {
                console.error(error);
                formResp.code = error.code || 400;
            }
        }
    }
    return formResp;
}

export async function getNewPromoInfo(sponsorId, promoId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}/promotions/${promoId}`,
        newFetchOpts({ token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export async function getNewPromoSetting(sponsorId, promoId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}/promotions/${promoId}/settings`,
        newFetchOpts({ token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export async function getPromoSetting(promotionId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/promotion/${promotionId}/promo-setting`,
        fetchOpts({ method: 'GET', token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export function setFormPublish(formVersion, sponsorId, formId) {
    return formVersion === 0 ? setOldFormPublish(formId) : setNewFormPublish(sponsorId, formId);
}

async function setOldFormPublish(formId) {
    const body = JSON.stringify({ status: 1 });
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/form/${formId}/status`,
        fetchOpts({ method: 'PATCH', body, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

async function setNewFormPublish(sponsorId, formId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${formId}/status`,
        newFetchOpts({ method: 'PATCH', body: { status: 1 }, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export function updateForm(formVersion, sponsorId, formId, body) {
    return formVersion === 0 ? updateOldForm(formId, body) : updateNewForm(sponsorId, formId, body);
}

async function updateOldForm(formId, data) {
    const body = JSON.stringify(data);
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/form/${formId}`,
        fetchOpts({ method: 'PATCH', body, token })
    ).then(res => {
        if (res.status >= 400) {
            return Promise.reject(new Error(`UpdateForm: ${res.status}`));
        }
        hasPermission(res.status);
        return res.json();
    });
}

async function updateNewForm(sponsorId, formId, body) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${formId}`,
        newFetchOpts({ method: 'POST', body, token })
    ).then(res => {
        if (res.status >= 400) {
            return Promise.reject(new Error(`UpdateForm: ${res.status}`));
        }
        hasPermission(res.status);
        return res.json();
    });
}

export function updateHiddenField(formVersion, sponsorId, formId, body) {
    return formVersion === 0
        ? updateOldHiddenField(formId, body)
        : updateNewHiddenField(sponsorId, formId, body);
}

async function updateNewHiddenField(sponsorId, id, data) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${id}/hidden-fields`,
        newFetchOpts({ method: 'POST', body: { hidden: data }, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

async function updateOldHiddenField(id, data) {
    const body = JSON.stringify({ hidden: data });
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/form/${id}/hidden-fields`,
        fetchOpts({ method: 'POST', body, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export function getGlobalSectionList(sponsorId) {
    return getNewGlobalSectionList(sponsorId);
}

async function getNewGlobalSectionList(sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/global-section-list`,
        newFetchOpts({ token })
    ).then(res => res.json());
}

export function getGlobalSectionSectionData(data, sponsorId) {
    return getNewGlobalSectionSectionData(data, sponsorId);
}

async function getNewGlobalSectionSectionData(data, sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/global-sections?id=${data}`,
        newFetchOpts({ token })
    ).then(r => r.json());
}

export async function updateGlobalSectionData(data, sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/global-sections`,
        newFetchOpts({ method: 'PATCH', body: data, token })
    ).then(r => r.json());
}

export async function getAccountInfo() {
    const token = await getFirebaseIdToken();
    return fetch(`${FEVER_API_ENDPOINT}/services/admin`, newFetchOpts({ token }))
        .then(r => r.json())
        .then(({ data }) => data);
}

export async function getAccountRoleInfo(sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}/member/role`,
        newFetchOpts({ token })
    )
        .then(r => r.json())
        .then(({ data }) => data);
}

export async function getPartnerInfo() {
    const token = await getFirebaseIdToken();
    return fetch(`${FEVER_API_ENDPOINT}/services/partner`, newFetchOpts({ token }))
        .then(resp => resp.json())
        .then(({ data: partnerData }) => ({
            ...partnerData
        }));
}

export async function getSponsorInfo(sponsorId) {
    const token = await getFirebaseIdToken();
    return Promise.all([
        fetch(`${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}`, newFetchOpts({ token })),
        fetch(
            `${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}/settings`,
            newFetchOpts({ token })
        ),
        fetch(
            `${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}/plans/orders`,
            newFetchOpts({ token })
        )
    ])
        .then(resp => Promise.all(resp.map(r => r.json())))
        .then(([{ data: sponsorData }, { data: sponsorSettingData }, { data: plans }]) => ({
            ...sponsorData,
            plans,
            settings: sponsorSettingData
        }));
}

/**
 * @param  {} formId - form Id
 * @param  {} data - crmName, sectionid, seq, type, pageId, pageSeq
 */
export function transCommonToGlobalSection(formVersion, sponsorId, formId, data) {
    return formVersion === 0
        ? transOldCommonToGlobalSection(formId, data)
        : transNewCommonToGlobalSection(sponsorId, formId, data);
}

async function transNewCommonToGlobalSection(sponsorId, formId, data) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${formId}/global-sections`,
        newFetchOpts({ method: 'POST', body: { data }, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

async function transOldCommonToGlobalSection(id, data) {
    const body = JSON.stringify({ data });
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/form/${id}/to-global-section`,
        fetchOpts({ method: 'POST', body, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export async function createHash(sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/form/test-code`,
        fetchOpts({ method: 'POST', sponsorId, token })
    ).then(res => {
        hasPermission(res.status);
        return res.json();
    });
}

export async function getTestcode(sponsorId) {
    const token = await getFirebaseIdToken();
    return fetch(
        `${FEVER_API_ENDPOINT}/services/sponsors/${sponsorId}/previewcode`,
        newFetchOpts({ token })
    ).then(res => res.json());
}

export async function uploadFile({ id, sponsorId, file, type, fileName }) {
    const token = await getFirebaseIdToken();
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('module', type);
    formData.append('formId', id);
    const fetchOptions = newFetchOpts({ method: 'POST', formData, token });
    return fetch(`${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/upload`, fetchOptions).then(res =>
        res.json()
    );
}

export async function getRedirectUrlAPI(formId, promoId, serialNumber, token) {
    return fetch(
        `${FEVER_API_ENDPOINT}/form/${formId}/redirect-url?promoid=${promoId}&serialno=${serialNumber}`,
        fetchOpts({ method: 'GET', token })
    ).then(res => res.json());
}

const hasPermission = status => {
    // token validate fail
    if (status === 401) {
        toLoginPage();
    }
};

/*
 * https://app.clickup.com/t/86etku3mr
 * @param {*} sponsorId
 * @param {*} formId
 * @param {*} sectionId
 * @returns
 */
export async function getOptionsApi({ sponsorId, formId, sectionId }) {
    if (!sponsorId || !formId || !sectionId) {
        return Promise.reject(new Error('Invalid parameters'));
    }
    const token = await getFirebaseIdToken();

    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${formId}/sections/${sectionId}/options`,
        newFetchOpts({ token })
    ).then(res => res.json());
}

/**
 * https://app.clickup.com/t/86etm5d0d
 * @param {*} sponsorId
 * @param {*} formId
 * @param {*} sectionId
 * @param {*} file 上傳的檔案
 * @returns
 */
export async function batchUploadOptionsFile({ sponsorId, formId, sectionId, file }) {
    if (!sponsorId || !formId || !sectionId) {
        return Promise.reject(new Error('Invalid parameters'));
    }
    const token = await getFirebaseIdToken();

    const formData = new FormData();
    formData.append('file', file, file.name);
    const body = formData;
    return fetch(
        `${FEVER_API_ENDPOINT}/sponsors/${sponsorId}/forms/${formId}/sections/${sectionId}/options/upload`,
        newFetchOpts({ method: 'POST', body, token })
    ).then(res => res.json());
}
